/**
 * @module components/Settings/ProfileTab
 * @description Pestaña de configuración del perfil de usuario.
 *
 * Permite al usuario actualizar su nombre, unidad organizacional y avatar.
 * El avatar se sube al bucket 'avatars' de Supabase Storage usando `uploadAvatar()`.
 * Los cambios se persisten en la tabla `users` de PostgreSQL via `AuthContext.updateProfile()`.
 *
 * Usa `forwardRef` + `useImperativeHandle` para exponer `saveChanges()` al `SettingsModal`
 * que centraliza el guardado de todas las pestañas con un solo botón "Guardar".
 *
 * @see SettingsModal que llama a saveChanges() al hacer clic en "Guardar"
 * @see supabase.uploadAvatar para la subida del avatar al Storage
 */
import React, { useState, useRef, forwardRef, useImperativeHandle } from 'react';
import { Camera, Mail, Building, Tag, User as UserIcon, Loader } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { uploadAvatar, deleteAvatar } from '../../lib/supabase';

export const ProfileTab = forwardRef<{ saveChanges: () => Promise<void> }>((props, ref) => {
  const { user, updateProfile } = useAuth();
  const [name, setName] = useState(user?.name || '');
  const [email, setEmail] = useState(user?.email || '');
  const [unit, setUnit] = useState(user?.unit || '');
  const [isUploading, setIsUploading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [saveSuccess, setSaveSuccess] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleAvatarUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !user) return;

    setIsUploading(true);
    setUploadError('');

    try {
      // Subir nuevo avatar
      const { url, error } = await uploadAvatar(user.id, file);

      if (error) {
        setUploadError(error.message);
        return;
      }

      if (url) {
        // Eliminar avatar anterior si existe
        if (user.avatar_url) {
          await deleteAvatar(user.avatar_url);
        }

        // Actualizar perfil con nuevo avatar
        await updateProfile({ avatar_url: url });
        setUploadError('');
      }
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : 'Error subiendo avatar');
    } finally {
      setIsUploading(false);
      // Limpiar input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDeleteAvatar = async () => {
    if (!user?.avatar_url || !window.confirm('¿Estás seguro de eliminar tu foto de perfil?')) return;

    setIsUploading(true);
    setUploadError('');

    try {
      await deleteAvatar(user.avatar_url);
      await updateProfile({ avatar_url: '' });
    } catch (err) {
      setUploadError('Error eliminando avatar');
      console.error('Error eliminando avatar:', err);
    } finally {
      setIsUploading(false);
    }
  };

  const handleSaveProfile = async () => {
    if (!user) return;

    setIsSaving(true);
    setSaveSuccess(false);

    try {
      await updateProfile({
        name: name.trim(),
        unit: unit.trim()
      });
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      console.error('Error guardando perfil:', err);
      alert('Error guardando cambios. Por favor intenta de nuevo.');
    } finally {
      setIsSaving(false);
    }
  };

  const hasChanges = () => {
    return name.trim() !== user?.name || unit.trim() !== user?.unit;
  };

  // Expose saveChanges method to parent via ref
  useImperativeHandle(ref, () => ({
    saveChanges: handleSaveProfile
  }));

  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'admin': return 'Administrador';
      case 'technical_referent': return 'Referente Técnico';
      case 'auditor': return 'Auditor';
      case 'viewer': return 'Visualizador';
      default: return role;
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Información del Perfil
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
          Actualiza tu información personal y foto de perfil
        </p>
      </div>

      {/* Avatar section */}
      <div className="flex items-center gap-6 pb-6 border-b border-gray-200 dark:border-gray-700">
        <div className="relative">
          {/* Avatar display */}
          {user?.avatar_url ? (
            <img
              src={user.avatar_url}
              alt={user.name}
              className="w-20 h-20 rounded-full object-cover border-4 border-gray-200 dark:border-gray-600"
            />
          ) : (
            <div className="w-20 h-20 rounded-full bg-blue-500 flex items-center justify-center border-4 border-gray-200 dark:border-gray-600">
              <UserIcon className="w-10 h-10 text-white" />
            </div>
          )}

          {/*
            FUNCIONALIDAD DESHABILITADA TEMPORALMENTE
            Razón: Problema con RLS policies de Supabase Storage

            Para reactivar:
            1. Descomentar este bloque completo
            2. Verificar que el bucket 'avatars' existe en Supabase
            3. Configurar políticas RLS correctas en storage.objects
            4. Ejecutar: node scripts/fix-storage-policies.js
          */}
          {/* <button
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploading}
            className="absolute bottom-0 right-0 p-2 bg-white dark:bg-gray-700 rounded-full shadow-lg border-2 border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Camera className="w-4 h-4 text-gray-600 dark:text-gray-300" />
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleAvatarUpload}
            className="hidden"
          /> */}
        </div>
        <div>
          <h4 className="font-medium text-gray-900 dark:text-white mb-1">Foto de perfil</h4>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
            Funcionalidad temporalmente deshabilitada
          </p>

          {/* Mensaje informativo */}
          <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
            <p className="text-xs text-amber-800 dark:text-amber-200">
              La función de cambio de foto está temporalmente deshabilitada debido a configuración de Storage.
              Se reactivará en una próxima actualización.
            </p>
          </div>

          {/*
            BOTONES DESHABILITADOS TEMPORALMENTE
          */}
          {/* {uploadError && (
            <p className="text-sm text-red-600 dark:text-red-400 mb-3">{uploadError}</p>
          )}
          <div className="flex gap-2">
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploading}
              className="px-3 py-1.5 text-sm font-medium text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cambiar foto
            </button>
            {user?.avatar_url && (
              <button
                onClick={handleDeleteAvatar}
                disabled={isUploading}
                className="px-3 py-1.5 text-sm font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Eliminar
              </button>
            )}
          </div> */}
        </div>
      </div>

      {/* Form fields */}
      <div className="space-y-4">
        {/* Name */}
        <div>
          <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Nombre completo
          </label>
          <div className="relative">
            <UserIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              id="name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Tu nombre completo"
            />
          </div>
        </div>

        {/* Email */}
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Correo electrónico
          </label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              id="email"
              type="email"
              value={email}
              disabled
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 cursor-not-allowed"
              placeholder="tu@email.com"
            />
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            El email no se puede cambiar directamente. Contacta al administrador.
          </p>
        </div>

        {/* Unit */}
        <div>
          <label htmlFor="unit" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Unidad / Departamento
          </label>
          <div className="relative">
            <Building className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              id="unit"
              type="text"
              value={unit}
              onChange={(e) => setUnit(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Ej: TI, Seguridad"
            />
          </div>
        </div>

        {/* Role (read-only) */}
        <div>
          <label htmlFor="role" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Rol
          </label>
          <div className="relative">
            <Tag className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              id="role"
              type="text"
              value={getRoleLabel(user?.role || '')}
              disabled
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 cursor-not-allowed"
            />
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            El rol solo puede ser modificado por un administrador
          </p>
        </div>
      </div>

      {/* Save button */}
      {hasChanges() && (
        <div className="flex items-center gap-3 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
          <div className="flex-1">
            <p className="text-sm font-medium text-blue-900 dark:text-blue-100">
              Tienes cambios sin guardar
            </p>
            <p className="text-xs text-blue-700 dark:text-blue-300">
              Haz clic en "Guardar Cambios" en la parte inferior para aplicar los cambios
            </p>
          </div>
        </div>
      )}

      {saveSuccess && (
        <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
          <p className="text-sm font-medium text-green-900 dark:text-green-100">
            ✓ Cambios guardados exitosamente
          </p>
        </div>
      )}

      {/* Password section */}
      <div className="pt-6 border-t border-gray-200 dark:border-gray-700">
        <h4 className="font-medium text-gray-900 dark:text-white mb-2">Seguridad</h4>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
          Mantén tu cuenta segura
        </p>
        <button
          disabled
          className="px-4 py-2 text-sm font-medium text-gray-400 dark:text-gray-500 bg-gray-100 dark:bg-gray-800 rounded-lg cursor-not-allowed"
        >
          Cambiar contraseña (Próximamente)
        </button>
      </div>
    </div>
  );
});
